import Constants from 'expo-constants';
import * as Updates from 'expo-updates';

import { Sentry } from '@/util/sentry';

/**
 * Safely checks for and fetches OTA updates with proper error tracking.
 *
 * Wraps the Expo Updates API calls with comprehensive error handling
 * and Sentry tracking for debugging production issues.
 *
 * @returns Promise resolving to whether an update was available and fetched
 */
export async function safeCheckAndFetchUpdate(): Promise<{
  isAvailable: boolean;
  fetchedSuccessfully: boolean;
  error: string | null;
}> {
  let isAvailable = false;

  let fetchedSuccessfully = false;

  let error: string | null = null;

  try {
    const result = await Updates.checkForUpdateAsync();

    isAvailable = result.isAvailable;

    if (isAvailable) {
      await Updates.fetchUpdateAsync();

      fetchedSuccessfully = true;
    }

    return { isAvailable, fetchedSuccessfully, error };
  } catch (err) {
    error = err instanceof Error ? err.message : 'Unknown update error';

    console.warn('Update check/fetch failed:', error);

    Sentry.captureException(err, {
      tags: {
        scope: 'ota-update',
        operation: isAvailable ? 'fetch' : 'check',
      },
      extra: {
        currentRuntime: Constants.expoConfig?.runtimeVersion,
        currentVersion: Constants.expoConfig?.version,
        currentBuild:
          Constants.expoConfig?.ios?.buildNumber || Constants.expoConfig?.android?.versionCode,
        updateChannel: Constants.expoConfig?.updates?.requestHeaders?.['expo-channel-name'],
      },
    });

    return { isAvailable: false, fetchedSuccessfully: false, error };
  }
}
